import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { mdiClose, mdiPlus } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from '../ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover'

export type CodeOption = { code: string; label: string }

/**
 * A multi-select over a known vocabulary of codes (subtypes, rules, asset
 * types, document types…): the picked codes show as removable chips and an
 * add button opens a searchable list. A picked code the vocabulary does not
 * know (an old seed, an organization row) stays visible, flagged, so it can
 * be removed. Spec 2026-10-28-15 § 10.
 */
export function CodeMultiPicker({ value, options, onChange, placeholder, disabled, single, testId }: {
    value: string[]
    options: CodeOption[]
    onChange: (codes: string[]) => void
    placeholder: string
    disabled?: boolean
    /** Keep at most one code: picking replaces it. */
    single?: boolean
    testId: string
}) {
    const { t } = useTranslation('scenarios')
    const [open, setOpen] = useState(false)
    const byCode = useMemo(() => new Map(options.map((o) => [o.code, o])), [options])
    const picked = new Set(value)
    const remaining = options.filter((o) => !picked.has(o.code))

    const add = (code: string) => {
        onChange(single ? [code] : [...value, code])
        setOpen(false)
    }

    return (
        <div className="space-y-1" data-testid={testId}>
            <span className="text-xs text-muted-foreground">{placeholder}</span>
            <div className="flex flex-wrap items-center gap-1">
                {value.map((code) => {
                    const known = byCode.get(code)
                    return (
                        <Badge
                            key={code}
                            variant={known ? 'secondary' : 'outline'}
                            className="gap-1"
                            title={known ? code : t('editor.unknown_code')}
                            data-testid={`${testId}-chip-${code}`}
                        >
                            {known?.label ?? code}
                            {!disabled && (
                                <button
                                    type="button"
                                    onClick={() => onChange(value.filter((c) => c !== code))}
                                    aria-label={t('actions.remove')}
                                    data-testid={`${testId}-remove-${code}`}
                                >
                                    <AppIcon path={mdiClose} size="xs" />
                                </button>
                            )}
                        </Badge>
                    )
                })}
                {value.length === 0 && <span className="text-xs text-muted-foreground">{t('editor.none')}</span>}
                {!disabled && (
                    <Popover open={open} onOpenChange={setOpen}>
                        <PopoverTrigger asChild>
                            <Button type="button" size="icon" variant="ghost" className="h-7 w-7" aria-label={t('actions.add')} title={t('actions.add')} data-testid={`${testId}-add`}>
                                <AppIcon path={mdiPlus} size="sm" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-80 p-0" align="start">
                            <Command>
                                <CommandInput placeholder={t('editor.search')} data-testid={`${testId}-search`} />
                                <CommandList>
                                    <CommandEmpty>{t('editor.no_match')}</CommandEmpty>
                                    {remaining.map((o) => (
                                        <CommandItem key={o.code} value={`${o.label} ${o.code}`} onSelect={() => add(o.code)} data-testid={`${testId}-option-${o.code}`}>
                                            <span className="flex-1 truncate">{o.label}</span>
                                            <span className="ml-2 font-mono text-[10px] text-muted-foreground">{o.code}</span>
                                        </CommandItem>
                                    ))}
                                </CommandList>
                            </Command>
                        </PopoverContent>
                    </Popover>
                )}
            </div>
        </div>
    )
}
